import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api, { offerApi } from '../services/api';

export default function SignedDocumentPage() {
  const { signatureId } = useParams();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [joiningStatus, setJoiningStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // First check joining status
    offerApi.documentStatus(signatureId)
      .then(res => {
        const status = res.data.joiningStatus;
        setJoiningStatus(status);
        if (status === 'JOINED') {
          // Only fetch PDF if joined
          return api.get(`/offer/document/${signatureId}`, { responseType: 'blob' })
            .then(r => {
              const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
              setPdfUrl(url);
            });
        }
      })
      .catch(() => setError('Document not found or unavailable.'))
      .finally(() => setLoading(false));
  }, [signatureId]);

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = 'signed-offer-letter.pdf';
    a.click();
  };

  if (loading) return (
    <div style={s.page}>
      <div style={s.card}><div style={{ textAlign: 'center', color: '#888' }}>Loading...</div></div>
    </div>
  );

  if (error) return (
    <div style={s.page}>
      <div style={s.card}><div style={{ textAlign: 'center', color: '#dc2626' }}>❌ {error}</div></div>
    </div>
  );

  // ── LOCKED — candidate hasn't joined yet ──────────────────────────────────
  if (joiningStatus !== 'JOINED') {
    const messages = {
      PENDING_JOIN: {
        icon: '🔒',
        title: 'Offer Letter Locked',
        body: 'Your signed offer letter will be available once your joining is confirmed by the HR team. Please report on your start date to unlock access.',
        color: '#f59e0b',
      },
      NO_SHOW: {
        icon: '❌',
        title: 'Access Unavailable',
        body: 'Your offer letter access has been revoked as the joining was not completed. Please contact HR for assistance.',
        color: '#ef4444',
      },
      WITHDRAWN: {
        icon: '↩️',
        title: 'Offer Withdrawn',
        body: 'This offer has been withdrawn. Please contact HR for more information.',
        color: '#6b7280',
      },
    };
    const msg = messages[joiningStatus] || messages.PENDING_JOIN;

    return (
      <div style={s.page}>
        <div style={s.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 24 }}>🔐</div>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>SecureOffer</div>
              <div style={{ color: '#aaa', fontSize: 12 }}>Signed Offer Letter</div>
            </div>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 48, maxWidth: 480, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 64, marginBottom: 20 }}>{msg.icon}</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>{msg.title}</h2>
            <p style={{ color: '#666', lineHeight: 1.7, marginBottom: 24 }}>{msg.body}</p>
            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 20px', fontSize: 13, color: '#888' }}>
              ✅ Your digital signature is securely recorded and tamper-evident.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── UNLOCKED — candidate has joined ──────────────────────────────────────
  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 24 }}>🔐</div>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>SecureOffer</div>
            <div style={{ color: '#aaa', fontSize: 12 }}>Signed Offer Letter</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ background: '#10b981', color: '#fff', padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
            ✅ Digitally Signed
          </span>
          <span style={{ background: '#7c83fd', color: '#fff', padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
            🎉 Joined
          </span>
          <button style={s.downloadBtn} onClick={handleDownload}>
            ⬇ Download PDF
          </button>
        </div>
      </div>

      <div style={s.viewerWrap}>
        <iframe src={pdfUrl} style={s.iframe} title="Signed Offer Letter" />
      </div>

      <div style={s.footer}>
        This is your permanently accessible signed offer letter. Welcome to the team!
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#1a1a2e', display: 'flex', flexDirection: 'column' },
  card: { maxWidth: 600, margin: '80px auto', background: '#fff', borderRadius: 12, padding: 40 },
  header: { background: '#1a1a2e', borderBottom: '1px solid #333', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  downloadBtn: { padding: '8px 20px', background: '#7c83fd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  viewerWrap: { flex: 1, padding: '0 32px', display: 'flex', flexDirection: 'column' },
  iframe: { flex: 1, width: '100%', height: 'calc(100vh - 120px)', border: 'none', borderRadius: 8, marginTop: 16 },
  footer: { textAlign: 'center', color: '#555', fontSize: 11, padding: '12px 32px 16px' },
};
